import React, { useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Users } from 'lucide-react';

const ActivityAttendees = ({ activityId, maxAttendees }) => {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatorInfo, setCreatorInfo] = useState(null);

  useEffect(() => {
    if (!activityId) return;

    // Referencia al documento de la actividad
    const activityRef = doc(db, "activities", activityId);

    // Configurar escucha en tiempo real
    const unsubscribe = onSnapshot(
      activityRef,
      async (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const activityData = docSnapshot.data();
            const attendeeIds = activityData.attendees || [];
            const creatorUID = activityData.creatorUID;

            console.log("Attendee IDs:", attendeeIds); // Debug

            // Obtener información completa del creador desde Users
            let creatorNickname = "";
            try {
              const creatorDoc = await getDoc(doc(db, "Users", creatorUID));
              if (creatorDoc.exists()) {
                const creatorData = creatorDoc.data();
                creatorNickname = creatorData.nickname || "";
              }
            } catch (err) {
              console.error("Error fetching creator info:", err);
            }

            // Guardar la información del creador
            setCreatorInfo({
              uid: creatorUID,
              name: activityData.creatorName || 'Organizer',
              photo: activityData.creatorPhoto || "https://via.placeholder.com/40",
              nickname: creatorNickname
            });

            // Si no hay asistentes, terminar temprano
            if (attendeeIds.length === 0) {
              setAttendees([]);
              setLoading(false);
              return;
            }

            // Este enfoque funciona mejor para Firestore
            // Obtenemos todos los usuarios de una vez
            const usersRef = collection(db, "Users"); // Colección "Users" con mayúscula
            const attendeesData = [];

            // Para cada ID de asistente, buscamos su documento
            for (const attendeeId of attendeeIds) {
              try {
                console.log(`Fetching user with ID: ${attendeeId}`); // Debug

                // Primero intentamos con el método directo
                const userDocRef = doc(usersRef, attendeeId);
                const userDoc = await getDoc(userDocRef);

                if (userDoc.exists()) {
                  console.log(`Found user: ${attendeeId}`, userDoc.data()); // Debug
                  const userData = userDoc.data();
                  attendeesData.push({
                    uid: attendeeId,
                    name: `${userData.nombre || ""} ${userData.apellido || ""}`.trim() || userData.name || userData.displayName || 'User ' + attendeeId.slice(0, 5),
                    nickname: userData.nickname || "",
                    photo: userData.photoUrl || userData.photoURL || "https://via.placeholder.com/40",
                    isCreator: attendeeId === creatorUID
                  });
                } else {
                  console.log(`User doc doesn't exist for ID: ${attendeeId}`); // Debug

                  // Si no encontramos por ID exacto, intentamos buscar por uid como campo
                  const q = query(usersRef, where("uid", "==", attendeeId));
                  const querySnapshot = await getDocs(q);

                  if (!querySnapshot.empty) {
                    const userData = querySnapshot.docs[0].data();
                    console.log(`Found user by query: ${attendeeId}`, userData); // Debug
                    attendeesData.push({
                      uid: attendeeId,
                      name: `${userData.nombre || ""} ${userData.apellido || ""}`.trim() || userData.name || userData.displayName || 'User ' + attendeeId.slice(0, 5),
                      nickname: userData.nickname || "",
                      photo: userData.photoUrl || userData.photoURL || "https://via.placeholder.com/40",
                      isCreator: attendeeId === creatorUID
                    });
                  } else {
                    // Si todo falla, añadimos un usuario genérico
                    console.log(`Couldn't find user: ${attendeeId}`); // Debug
                    attendeesData.push({
                      uid: attendeeId,
                      name: 'User ' + attendeeId.slice(0, 5),
                      nickname: "",
                      photo: "https://via.placeholder.com/40",
                      isCreator: attendeeId === creatorUID
                    });
                  }
                }
              } catch (err) {
                console.error(`Error fetching user ${attendeeId}:`, err);
                attendeesData.push({
                  uid: attendeeId,
                  name: 'User ' + attendeeId.slice(0, 5),
                  photo: "https://via.placeholder.com/40",
                  isCreator: attendeeId === creatorUID
                });
              }
            }

            // Ordenar para que el creador aparezca primero
            attendeesData.sort((a, b) => (a.isCreator ? -1 : b.isCreator ? 1 : 0));
            console.log("Final attendees data:", attendeesData); // Debug

            setAttendees(attendeesData);
            setLoading(false);
          }
        } catch (err) {
          console.error("Error fetching attendees:", err);
          setError("Failed to load attendees");
          setLoading(false);
        }
      },
      (err) => {
        console.error("Error listening to activity:", err);
        setError("Error monitoring attendees");
        setLoading(false);
      }
    );

    // Limpiar el listener cuando el componente se desmonte
    return () => unsubscribe();
  }, [activityId]);

  if (loading) {
    return (
      <div className="bg-white p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-gray-400" />
          <span className="font-medium">Attendees</span>
          <div className="ml-auto">
            <div className="h-5 w-10 bg-gray-200 animate-pulse rounded-md"></div>
          </div>
        </div>
        <div className="space-y-3">
          {[...Array(2)].map((_, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 animate-pulse rounded w-24"></div>
                <div className="h-3 bg-gray-200 animate-pulse rounded w-16 mt-1"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-5 h-5 text-gray-400" />
          <span className="font-medium">Attendees</span>
        </div>
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-gray-400" />
          <span className="font-medium">Attendees</span>
        </div>
        <span className="text-sm text-gray-500">
          {attendees.length} / {maxAttendees}
        </span>
      </div>

      {attendees.length === 0 && !creatorInfo ? (
        <p className="text-gray-500 text-sm">No attendees yet</p>
      ) : (
        <div className="space-y-3">
          {/* Creator info */}
          {creatorInfo && (
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 rounded-full border-2 border-orange-500 -m-1"></div>
                <img
                  src={creatorInfo.photo}
                  alt={creatorInfo.name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                />
              </div>
              <div>
                <p className="font-medium">{creatorInfo.name}</p>
                {creatorInfo.nickname ? (
                  <p className="text-sm text-gray-500">{creatorInfo.nickname}</p>
                ) : (
                  <p className="text-sm text-gray-500">Organizer</p>
                )}
              </div>
            </div>
          )}

          {/* Other attendees */}
          {attendees
            .filter(attendee => !attendee.isCreator)
            .map(attendee => (
              <div key={attendee.uid} className="flex items-center gap-3">
                <img
                  src={attendee.photo}
                  alt={attendee.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium">{attendee.name}</p>
                  {attendee.nickname && (
                    <p className="text-sm text-gray-500">{attendee.nickname}</p>
                  )}
                  {!attendee.nickname && (
                    <p className="text-sm text-gray-500">Participant</p>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ActivityAttendees;